/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2014, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./BaseView',
	'react',
	'react-dom',
	'../../app/nls/StringResources',
	'underscore',
	'../layouts/TabLayout',
	'../layouts/CustomLayout',
	'../layouts/InfographicsLayout',
	'../layouts/SinglePageLayout',
	'../views/NavigationPickerTabs',
	'../../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'./util/InstrumentationUtil',
	'../../features/dashboard/dashboardTemplates/api/impl/DashboardTemplatesImpl',
	'../TemplateLayoutGenerator',
	'../../lib/@waca/dashboard-common/dist/ui/dialogs/MessageDialog',
	'../../app/util/DeepClone' // TODO: Remove the use of DeepClone for something else
], function(PageView,
	React,
	ReactDOM,
	stringResources,
	_,
	TabLayout,
	CustomLayout,
	InfographicsLayout,
	SinglePageLayout,
	NavigationPickerTabs,
	UniqueId,
	InstrumentationUtil,
	DashboardTemplates,
	TemplateLayoutGenerator,
	MessageDialog) {

	const CreateAuthoredView = PageView.extend({
		boardName: '',

		events: {
			'input .editNameInput': 'onBoardNameChange'
		},

		$navPicker: null,

		/**
		* Map of available navigation layouts
		*
		* For the moment, these also force the widget layout
		*
		**/
		_navigationLayouts: {
			tab: TabLayout,
			infographics: InfographicsLayout,
			singlePage: SinglePageLayout,
			custom: CustomLayout
		},

		init: function(options, appView) {
			this.sources = options.sources;
			this.ui_appbar = options.ui_appbar !== false;
			this.containerAppOptions = options.containerAppOptions;
			this._appView = appView;

			CreateAuthoredView.inherited('init', this, arguments);

			const _dashboardTemplates = new DashboardTemplates({
				getContentSvc: this.glassContext.getSvc('.Content'),
				logger: this.glassContext.getSvc('.Logger'),
				featureChecker: this.glassContext.getCoreSvc('.FeatureChecker')
			});
			this.dashboardTemplates = _dashboardTemplates.getAPI();

			this.boardName = stringResources.get('defaultName');
		},

		_updateOptions: function() {
			if (this._appView && this._appView.content) {
				this.sources = this._appView.content.sources;
				this.ui_appbar = this._appView.content.ui_appbar !== false;
				this.containerAppOptions = this._appView.content.containerAppOptions;
			}
		},

		render: function() {
			return this.dashboardTemplates.listTemplates().then(templates => {
				this._customTemplates = templates;
				return this._render();
			});
		},

		_getNavPickerOptions: function() {
			let excludedTemplates = [];
			const logger = this.glassContext.getCoreSvc('.Logger');
			const tabNavModel = {
				dataId: 'tab',
				label: stringResources.get('createAvTabLabel')
			};
			const infographicNavModel = {
				dataId: 'infographics',
				label: stringResources.get('createAvInfographicsLabel')
			};
			const singlePageModel = {
				dataId: 'singlePage',
				label: stringResources.get('createAvSinglepageLabel')
			};
			const knownTemplates = {
				'tab': tabNavModel,
				'infographic': infographicNavModel,
				'singlePage': singlePageModel
			};
			const navModels = [];
			return this.glassContext.appController.findCollection('com.ibm.bi.dashboard.templates').then(templateItems => {
				if (Array.isArray(templateItems) && templateItems.length > 0) {
					templateItems.forEach(template => {
						if (knownTemplates[template.dataId]) {
							navModels.push(knownTemplates[template.dataId]);
							if(Array.isArray(template.excludedTemplates)) {
								excludedTemplates = excludedTemplates.concat(template.excludedTemplates);
							}
						} else {
							logger.error('Unknown template provided in collections');
						}
					});
				} else {
					navModels.push(tabNavModel, infographicNavModel, singlePageModel);
				}

				if (this._customTemplates && this._customTemplates.length) {
					navModels.push({
						dataId: 'custom',
						label: stringResources.get('createAvCustomLabel')
					});
				}

				return {
					navModels: navModels,
					excludeTemplates: excludedTemplates
				};
			});
		},

		_render: function() {
			this.$el.empty();
			this.createBoardView = document.createElement('div');
			this.createBoardView.className = 'createBoardView';
			this.$el.append(this.createBoardView);
			this._handleAppAndNavBarVisiblity();

			return this._getNavPickerOptions().then(options => {
				this._templateLayoutGenerator = new TemplateLayoutGenerator({
					dashboardTemplatesApi: this.dashboardTemplates,
					customTemplates: this._customTemplates,
					excludeTemplates: options.excludeTemplates
				});
				return this._templateLayoutGenerator.initialize().then(() => {
					this._navModels = options.navModels.map(model => {
						return {
							...model,
							templates: this._templateLayoutGenerator.getTemplates(model.dataId)
						};
					});
					return this._renderTabs();
				});
			});
		},

		_renderTabs: function() {
			return new Promise(resolve => {
				ReactDOM.render(
					<NavigationPickerTabs
						navModels={this._navModels}
						templateLayoutGenerator={this._templateLayoutGenerator}
						action={(layoutSpec, navModel) => this._createAuthoredView(layoutSpec, navModel)}
						showContextMenu={(template, pageX, pageY) => this._showContextMenu(template, pageX, pageY)}
						defaultNavigation='tab'
						onCancel={() => this.onCancelClick()}
						glassContext={this.glassContext}
						displayError={(error) => this._displayErrorMessage(error)}
					/>,
					this.createBoardView,
					resolve
				);
			});
		},

		_showContextMenu: function(template, pageX, pageY) {
			this.glassContext.appController.showContextMenu({
				position: {
					pageX: pageX,
					pageY: pageY
				},
				menuId: 'com.ibm.bi.dashboard.templateMenu',
				activeObject: {
					// Mocking a list control object expected by showContextMenu
					oListControl: {
						contentView: {
							isEnabledAction: function() {
								return true;
							}
						},
						getSelectedRows: function() {
							return [ template ];
						},
						removeSelectedRows: function() {
						}
					},
					aSelectedContext: [ template ]
				},
				options: [{
					id: template.name
				}]
			});
		},

		_handleAppAndNavBarVisiblity: function() {
			if (this.containerAppOptions) {
				this.glassContext.appController.currentAppView.$('.navbar').css('display', 'none');
				this.glassContext.appController.currentAppView.$('.appbar').css('display', 'none');
			}
		},

		/**
		 *	This function cleans up the events and DOM before removing the view
		 *	@public
		 **/
		remove: function() {
			if (this.createBoardView) {
				ReactDOM.unmountComponentAtNode(this.createBoardView);
			}
			CreateAuthoredView.inherited('remove', this, arguments);
		},

		getUrl: function() {
			return null;
		},

		getIcon: function() {
			return 'common-dashboard_24';
		},

		getIconColor: function() {
			return 'purple';
		},

		getTitle: function() {
			return stringResources.get('createDashboardTitle');
		},

		// Are the following four methods still used?
		onBoardNameChange: function() {
			this.setBoardName(this.$('.editNameInput').val());
		},
		getBoardName: function() {
			return this.boardName;
		},
		setBoardName: function(value) {
			this.boardName = value;
			if (!this.boardName) {
				this.$('.okButton').prop('disabled', true);
			} else {
				this.$('.okButton').prop('disabled', false);
			}
		},
		changeBoardName: function() {
			/* NO-OP */
		},

		// TODO: this.containerAppOptions && this.containerAppOptions.callbacks may not be needed. Review later
		onCancelClick: function() {
			this._updateOptions();
			if (this.containerAppOptions && this.containerAppOptions.callbacks) {
				this.containerAppOptions.callbacks.reject('CREATE_CANCELLED');
			}
			this.close();
		},

		/**
		 * @returns {Promise}
		 */
		_createAuthoredView: function(layoutSpec, navModel) {
			InstrumentationUtil.track(this.glassContext, 'created', {
				objectType: 'dashboard',
				sources: this.sources,
				type: 'Created Object'
			});
			this._selectedNavTemplate = this._navigationLayouts[navModel];
			if (navModel === 'custom') {
				return this.dashboardTemplates.getTemplate(layoutSpec.name, true).then((template) => {
					return this._createBoard(Object.assign(template.specification, { name: this.boardName }));
				});
			}
			var navTemplate = this._createNavTemplate(layoutSpec);
			var spec = this._createBoardSpec(navTemplate);
			return this._createBoard(spec);
		},

		_createNavTemplate: function(pageLayout) {
			var navTemplate = _.deepClone(this._selectedNavTemplate);

			if (!navTemplate.layout.items) {
				// the navigation layout is a single page
				let pageSize = navTemplate.layout.pageSize;
				let layoutPositioning = navTemplate.layout.layoutPositioning;
				let showGrid = navTemplate.layout.showGrid;
				let snapGrid = navTemplate.layout.snapGrid;
				let snapObjects = navTemplate.layout.snapObjects;

				navTemplate.layout = _.deepClone(pageLayout);
				if (pageSize) {
					navTemplate.layout.pageSize = _.deepClone(pageSize);
				}
				if (layoutPositioning) {
					navTemplate.layout.layoutPositioning = _.deepClone(layoutPositioning);
				}
				navTemplate.layout.showGrid = showGrid === true;
				navTemplate.layout.snapGrid = snapGrid === true;
				navTemplate.layout.snapObjects = snapObjects === true;
			} else {
				var navLayout = navTemplate.layout;
				var newItems = [];
				_.each(navLayout.items, function(item) {
					var newItem = _.deepClone(pageLayout);
					newItem.title = item.title;
					newItem.data = item.data;
					newItems.push(newItem);
				});
				navLayout.items = newItems;
			}

			return navTemplate;
		},

		/**
		 * @param {object} navLayout
		 *
		 * @returns {object}
		 *
		 * NOTE
		 * See the "id" property in 'carbonGrey10.json' from dashboard-common
		 * repo to know where the value "carbonGrey10" below comes from.
		 */
		_createBoardSpec: function(navLayout) {
			return {
				name: this.boardName,
				layout: navLayout.layout,
				theme: 'carbonGrey10'
			};
		},

		/**
		 * @returns {Promise}
		 */
		_createBoard: function(spec) {
			spec._meta = {
				bundleID: null
			};
			return this._openBoard(spec);
		},

		_openBoard: function(spec) {
			var id = spec._meta.bundleID;
			this._updateOptions();
			return this.glassContext.appController.openAppView(this._getDashboardPerspectiveName(), {
				content: {
					id: UniqueId.get('dashboard_'),
					boardId: id,
					boardSpec: spec,
					isAuthoringMode: true,
					ui_appbar: this.ui_appbar,
					containerAppOptions: this.containerAppOptions,
					sources: this.sources
				}
			}).then(function (view) {
				return view.onViewRendered();
			}).then((view) => {
				view.currentContentView.openDatasetpane();
				this.glassContext.appController.closeAppView('createBoard');
			});
		},

		_getDashboardPerspectiveName: function() {
			return 'dashboard';
		},

		close: function() {
			this.glassContext.appController.closeAppView('createBoard');
		},

		_displayErrorMessage: function(error) {
			const msgDialog = new MessageDialog({
				title: stringResources.get('errorMessageTitle'),
				message: error.toString()
			});
			msgDialog.render();
		},

		// Overide default setFocus function
		setFocus: function() {}
	});

	return CreateAuthoredView;
});
